import { useCallback, useState, type RefObject } from 'react';
import { useReducedMotion, type MotionValue } from 'framer-motion';
import { getTabs, getTabContent } from '@/components/ui/Cchatteaser/Cchatteaser.data';
import { DEFAULT_ACTIVE_TAB } from './chatteaser3.constants';
import { CHATTEASER3_ANIM_PHASE } from './chatteaser3Anim';
import { measureChatTeaser3Layout } from './chatteaser3Layout';
import { useChatteaser3AnimationTimers } from './useChatteaser3AnimationTimers';
import { useChatteaser3LayoutState } from './useChatteaser3LayoutState';
import { useChatteaser3Intro } from './chatteaser3Intro';
import {
  CHATTEASER3_SCROLL_MODE,
  useChatteaser3Scroll,
} from './useChatteaser3Scroll';
import {
  type ChatTeaser3ScrollMotion,
  useChatteaser3ScrollMotion,
} from './useChatteaser3ScrollMotion';
import { DEFAULT_MOBILE_TAB } from './chatteaser3MobileTabs';
import { useChatteaser3MobileSwipe } from './useChatteaser3MobileSwipe';

type AnimPhaseLocal = (typeof CHATTEASER3_ANIM_PHASE)[keyof typeof CHATTEASER3_ANIM_PHASE];
type MobileCarouselDirection = -1 | 1;

const DEFAULT_SPREAD = 190;
const MOBILE_TAB_EXIT_MS = 80;

interface Chatteaser3MobileRuntime {
  activeTab: number;
  animPhase: AnimPhaseLocal;
  carouselDirection: MobileCarouselDirection;
  displayedTab: number;
  handleSelectTab: (index: number) => void;
  intro: ReturnType<typeof useChatteaser3Intro>;
  layout: ReturnType<typeof measureChatTeaser3Layout>;
  layoutVersion: ReturnType<typeof useChatteaser3LayoutState>['layoutVersion'];
  messagesInnerRef: RefObject<HTMLDivElement | null>;
  onWheel: ReturnType<typeof useChatteaser3MobileSwipe>['onWheel'];
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  scrollMotion: ChatTeaser3ScrollMotion;
  scrollProgress: number;
  shouldReduceMotion: boolean;
  soloContentRef: RefObject<HTMLDivElement | null>;
  soloRef: RefObject<HTMLDivElement | null>;
  spread: number;
  swipeHandlers: ReturnType<typeof useChatteaser3MobileSwipe>['swipeHandlers'];
  tabContent: ReturnType<typeof getTabContent>;
  tabVariant: typeof DEFAULT_MOBILE_TAB;
  tabs: ReturnType<typeof getTabs>;
  unitedHeaderRef: RefObject<HTMLDivElement | null>;
  unitedScrollerRef: RefObject<HTMLDivElement | null>;
  wrapperRef: RefObject<HTMLDivElement | null>;
}

export function useChatteaser3MobileRuntime(): Chatteaser3MobileRuntime {
  const tabs = getTabs();
  const shouldReduceMotion = useReducedMotion() === true;
  const [activeTab, setActiveTab] = useState(DEFAULT_ACTIVE_TAB);
  const [carouselDirection, setCarouselDirection] = useState<MobileCarouselDirection>(1);
  const [displayedTab, setDisplayedTab] = useState(DEFAULT_ACTIVE_TAB);
  const [animPhase, setAnimPhase] = useState<AnimPhaseLocal>(CHATTEASER3_ANIM_PHASE.Idle);
  const scheduleDisplayedTab = useCallback((value: number): void => {
    setDisplayedTab(value);
  }, []);
  const { clearEnterTimer, clearExitTimer } = useChatteaser3AnimationTimers({
    activeTab,
    displayedTab,
    exitMs: MOBILE_TAB_EXIT_MS,
    scheduleDisplayedTab,
    setAnimPhase,
  });

  const {
    layout,
    layoutRef,
    layoutVersion,
    messagesInnerRef,
    soloContentRef,
    soloRef,
    unitedHeaderRef,
    unitedScrollerRef,
    wrapperRef,
  } = useChatteaser3LayoutState({
    displayedTab,
  });
  const { resetScroll, scrollContainerRef, scrollY } = useChatteaser3Scroll({
    maxScroll: layout.maxScroll,
    mode: CHATTEASER3_SCROLL_MODE.Container,
    wrapperRef,
  });
  const scrollMotion = useChatteaser3ScrollMotion({
    layoutRef,
    layoutVersion,
    scrollY,
  });

  const handleSelectTab = useCallback((index: number): void => {
    if (index === activeTab) return;
    clearEnterTimer();
    clearExitTimer();
    setCarouselDirection(index > activeTab ? 1 : -1);
    if (shouldReduceMotion) {
      setAnimPhase(CHATTEASER3_ANIM_PHASE.Idle);
      setActiveTab(index);
      setDisplayedTab(index);
      resetScroll();
      return;
    }
    setAnimPhase(CHATTEASER3_ANIM_PHASE.Exit);
    setActiveTab(index);
    resetScroll();
  }, [
    activeTab,
    clearEnterTimer,
    clearExitTimer,
    resetScroll,
    shouldReduceMotion,
  ]);

  const { swipeHandlers, onWheel, scrollProgress } = useChatteaser3MobileSwipe({
    activeTab,
    tabCount: tabs.length,
    onSelect: handleSelectTab,
  });

  const intro = useChatteaser3Intro();
  const tabContent = getTabContent(displayedTab);

  return {
    activeTab,
    animPhase,
    carouselDirection,
    displayedTab,
    handleSelectTab,
    intro,
    layout,
    layoutVersion,
    messagesInnerRef,
    onWheel,
    scrollContainerRef,
    scrollMotion,
    scrollProgress,
    shouldReduceMotion,
    soloContentRef,
    soloRef,
    spread: DEFAULT_SPREAD,
    swipeHandlers,
    tabContent,
    tabVariant: DEFAULT_MOBILE_TAB,
    tabs,
    unitedHeaderRef,
    unitedScrollerRef,
    wrapperRef,
  };
}
